/**
 * /characters/:id — edit a UGC character profile (issue #589).
 *
 * Loads the character, lets the admin edit name / description /
 * Higgsfield reference id / traits, and attach a reference still. The
 * image upload posts to /api/marketing/characters/:id/image which puts
 * to R2 and writes the URL back onto the row — so it's a separate,
 * immediate action, not part of the Save button.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiFetch, apiJson } from '../lib/api';
import type { Character } from '../lib/types';
import {
  TraitsEditor,
  rowsToTraits,
  traitsToRows,
  type TraitRow,
} from '../components/TraitsEditor';
import { HiggsfieldCommands } from '../components/HiggsfieldCommands';

export function CharacterDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [character, setCharacter] = useState<Character | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Editable form state, seeded from the loaded character.
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [higgsfieldId, setHiggsfieldId] = useState('');
  const [traitRows, setTraitRows] = useState<TraitRow[]>([]);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    apiJson<{ character: Character }>(`/api/marketing/characters/${id}`)
      .then((res) => {
        if (cancelled) return;
        const c = res.character;
        setCharacter(c);
        setName(c.name);
        setDescription(c.description);
        setHiggsfieldId(c.higgsfield_character_id ?? '');
        setTraitRows(traitsToRows(c.traits));
        setImageUrl(c.reference_image_url);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load character');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function save() {
    if (!id) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const res = await apiJson<{ character: Character }>(`/api/marketing/characters/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          higgsfield_character_id: higgsfieldId.trim() || null,
          traits: rowsToTraits(traitRows),
        }),
      });
      setCharacter(res.character);
      setSaved(true);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!id || !character) return;
    const ok = window.confirm(
      `Delete "${character.name}"?\n\nRemoves the character profile and its reference image. ` +
        'Jobs generated for this character keep their renders but lose the attribution. Cannot be undone.',
    );
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/marketing/characters/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`${res.status}: ${body || 'delete_failed'}`);
      }
      navigate('/characters', { replace: true });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Delete failed');
      setDeleting(false);
    }
  }

  if (loadError) {
    return (
      <div className="marketing-page">
        <div className="alert alert-error">{loadError}</div>
        <Link to="/characters" className="btn btn-ghost btn-sm" style={{ marginTop: 12 }}>
          ← Back to characters
        </Link>
      </div>
    );
  }
  if (!character) {
    return (
      <div className="marketing-page" style={{ paddingTop: 64, textAlign: 'center' }}>
        <div className="spinner" />
      </div>
    );
  }

  const dirty =
    name.trim() !== character.name ||
    description.trim() !== character.description ||
    (higgsfieldId.trim() || null) !== (character.higgsfield_character_id ?? null) ||
    JSON.stringify(rowsToTraits(traitRows)) !== JSON.stringify(character.traits);

  return (
    <div className="marketing-page">
      <header className="marketing-header">
        <div>
          <Link to="/characters" style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
            ← Characters
          </Link>
          <h1 style={{ marginTop: 4 }}>{character.name}</h1>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={deleting}
          onClick={handleDelete}
          style={{ color: 'crimson' }}
        >
          {deleting ? 'Deleting…' : 'Delete character'}
        </button>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(200px, 260px) 1fr',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <ReferenceImage
          characterId={character.id}
          imageUrl={imageUrl}
          onUploaded={(url) => setImageUrl(url)}
        />

        <section className="brief-panel" style={{ margin: 0 }}>
          <label className="field">
            <span>Name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <label className="field">
            <span>Description</span>
            <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>

          <label className="field">
            <span>Higgsfield character id</span>
            <input
              type="text"
              value={higgsfieldId}
              placeholder="Reference token from @higgsfield/cli"
              onChange={(e) => setHiggsfieldId(e.target.value)}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
          </label>

          <h2 style={{ marginTop: 24, fontSize: 18 }}>Traits</h2>
          <TraitsEditor rows={traitRows} onChange={setTraitRows} />

          {saveError && (
            <div className="alert alert-error" style={{ marginTop: 12 }}>
              {saveError}
            </div>
          )}
          {saved && !dirty && (
            <div className="alert" style={{ marginTop: 12 }}>
              Saved.
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || !dirty || !name.trim()}
              onClick={save}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </section>
      </div>

      <HiggsfieldCommands character={character} />
    </div>
  );
}

// ── reference image uploader ───────────────────────────────────────

function ReferenceImage({
  characterId,
  imageUrl,
  onUploaded,
}: {
  characterId: string;
  imageUrl: string | null;
  onUploaded: (url: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function upload(file: File) {
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiFetch(`/api/marketing/characters/${characterId}/image`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`${res.status}: ${text || 'upload_failed'}`);
      }
      const body = (await res.json()) as { reference_image_url: string };
      onUploaded(body.reference_image_url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="Character reference"
          style={{
            width: '100%',
            aspectRatio: '1 / 1',
            objectFit: 'cover',
            borderRadius: 10,
            background: '#000',
          }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            aspectRatio: '1 / 1',
            background: 'var(--color-surface-alt)',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-muted)',
            fontSize: 12,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          No image
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void upload(f);
        }}
      />
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        disabled={uploading}
        onClick={() => fileRef.current?.click()}
      >
        {uploading ? 'Uploading…' : imageUrl ? 'Replace image' : 'Upload reference image'}
      </button>
      {error && (
        <div className="alert alert-error" style={{ fontSize: 11 }}>
          {error}
        </div>
      )}
    </div>
  );
}
